if (!customElements.get('product-thumbnails')) {
  class ProductThumbnails extends HTMLElement {
    constructor() {
      super();
      this.thumbnails = this.querySelectorAll('[data-thumbnail]');
      this.previewContainer = document.getElementById(
        `ProductPreview-${this.dataset.sectionId}`
      );
      this.previewImage = this.previewContainer?.querySelector('img');
      this.previewWrapper = this.previewContainer?.querySelector('.product-hero__preview-wrapper');
      this.skeleton = this.previewContainer?.querySelector('.product-hero__preview-skeleton');
      this.setupListeners();
      this.setupLightbox();
    }

    setupListeners() {
      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', this.onThumbnailClick.bind(this));
      });
      this.setupSwipe();
    }

    setupSwipe() {
      const wrapper = this.previewContainer?.querySelector('.product-hero__preview-wrapper');
      if (!wrapper) return;

      let startX = 0;
      let startY = 0;

      wrapper.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      wrapper.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;

        // Only count horizontal swipes (dx dominates dy)
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;

        const visibleThumbs = Array.from(this.thumbnails).filter(
          (t) => t.style.display !== 'none'
        );
        const activeIdx = visibleThumbs.findIndex((t) => t.classList.contains('is-active'));

        if (dx < 0) {
          // Swipe left → next image
          const next = visibleThumbs[activeIdx + 1] || visibleThumbs[0];
          if (next) this.onThumbnailClick({ currentTarget: next }, 'next');
        } else {
          // Swipe right → previous image
          const prev = visibleThumbs[activeIdx - 1] || visibleThumbs[visibleThumbs.length - 1];
          if (prev) this.onThumbnailClick({ currentTarget: prev }, 'prev');
        }
      }, { passive: true });
    }

    onThumbnailClick(event, swipeDirection = null) {
      const thumb = event.currentTarget;

      // Update active state
      this.thumbnails.forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');

      // Update preview image
      if (this.previewImage) {
        // Show loading skeleton
        this.showSkeleton();

        this.previewImage.src = thumb.dataset.mediaSrc;
        if (thumb.dataset.mediaSrcset) {
          this.previewImage.srcset = thumb.dataset.mediaSrcset;
        }
        const thumbImg = thumb.querySelector('img');
        if (thumbImg?.alt) {
          this.previewImage.alt = thumbImg.alt;
        }

        // Hide skeleton when image loads
        this.previewImage.addEventListener('load', () => {
          this.hideSkeleton();

          // Trigger swipe animation if called from swipe gesture
          if (swipeDirection) {
            this.previewImage.classList.remove('swipe-in-next', 'swipe-in-prev');
            void this.previewImage.offsetWidth;
            this.previewImage.classList.add(
              swipeDirection === 'next' ? 'swipe-in-next' : 'swipe-in-prev'
            );
            this.previewImage.addEventListener('animationend', () => {
              this.previewImage.classList.remove('swipe-in-next', 'swipe-in-prev');
            }, { once: true });
          }
        }, { once: true });

        // Fallback: if image is already cached, load fires synchronously
        if (this.previewImage.complete) {
          this.hideSkeleton();
        }
      }
    }

    showSkeleton() {
      this.previewWrapper?.classList.add('is-loading');
      this.skeleton?.classList.add('is-visible');
    }

    hideSkeleton() {
      this.previewWrapper?.classList.remove('is-loading');
      this.skeleton?.classList.remove('is-visible');
    }

    setupLightbox() {
      const sectionId = this.dataset.sectionId;
      this.lightbox = document.getElementById(`ProductLightbox-${sectionId}`);
      this.lightboxImg = document.getElementById(`ProductLightboxImg-${sectionId}`);
      if (!this.lightbox || !this.previewImage) return;

      // Click on preview image opens lightbox
      this.previewImage.addEventListener('click', () => {
        this.lightboxImg.src = this.previewImage.src;
        this.lightboxImg.alt = this.previewImage.alt;
        this.lightbox.classList.add('is-open');
        this.lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });
      this.previewImage.style.cursor = 'zoom-in';

      // Click on backdrop (not on image) closes lightbox
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox || e.target.closest('.product-lightbox__close')) {
          this.closeLightbox();
        }
      });

      // Escape key closes lightbox
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.lightbox.classList.contains('is-open')) {
          this.closeLightbox();
        }
      });
    }

    closeLightbox() {
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    connectedCallback() {
      // Subscribe to variant change to filter thumbnails
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.onVariantChange.bind(this)
        );
      }
    }

    disconnectedCallback() {
      this.variantUnsubscriber?.();
    }

    onVariantChange(event) {
      const variantId = event?.data?.variant?.id;
      if (!variantId) return;

      let hasVariantImages = false;

      this.thumbnails.forEach((thumb) => {
        const attachedVariants = thumb.dataset.variantIds;
        if (attachedVariants) {
          const ids = attachedVariants.split(',');
          const isAttached = ids.includes(String(variantId));
          if (isAttached) hasVariantImages = true;
          thumb.style.display = isAttached ? '' : 'none';
        }
      });

      // If no images are attached to this variant, show all
      if (!hasVariantImages) {
        this.thumbnails.forEach((thumb) => {
          thumb.style.display = '';
        });
      }

      // Auto-select first visible thumbnail
      const firstVisible = this.querySelector('[data-thumbnail]:not([style*="display: none"])');
      if (firstVisible && !firstVisible.classList.contains('is-active')) {
        firstVisible.click();
      }
    }
  }

  customElements.define('product-thumbnails', ProductThumbnails);
}
